import { useRef, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useBranding, useUploadLogo, useDeleteLogo, useUpdateBusinessName } from '@/api/hooks/useSettings';
import { RequireRole } from '@/components/auth/RequireRole';
import { Upload, Trash2 } from 'lucide-react';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function BrandingPage() {
  return (
    <RequireRole roles={['super_admin']} label="Custom Branding">
      <BrandingPageBody />
    </RequireRole>
  );
}

function BrandingPageBody() {
  const { data: branding, isLoading } = useBranding();
  const uploadLogo = useUploadLogo();
  const deleteLogo = useDeleteLogo();
  const updateBusinessName = useUpdateBusinessName();
  useEffect(() => { document.title = 'Custom Branding — VeriGRC'; }, []);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [businessNameValue, setBusinessNameValue] = useState<string>('');
  const [businessNameInitialized, setBusinessNameInitialized] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Initialize business name from branding data once
  if (branding && !businessNameInitialized) {
    setBusinessNameValue(branding.businessName ?? '');
    setBusinessNameInitialized(true);
  }

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  function handleFileSelect(file: File) {
    setUploadError(null);
    const allowed = ['image/png', 'image/jpeg', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      setUploadError('File must be PNG, JPG, or SVG under 500 KB');
      return;
    }
    if (file.size > 524288) {
      setUploadError('File must be PNG, JPG, or SVG under 500 KB');
      return;
    }
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  }

  async function handleUpload() {
    if (!selectedFile) return;
    setUploadError(null);
    try {
      await uploadLogo.mutateAsync(selectedFile);
      setSelectedFile(null);
      setPreviewUrl(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      showToast('Logo uploaded successfully');
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0]?.message ?? 'File must be PNG, JPG, or SVG under 500 KB';
      setUploadError(msg);
    }
  }

  async function handleDeleteLogo() {
    try {
      await deleteLogo.mutateAsync();
      setShowDeleteConfirm(false);
      showToast('Logo removed');
    } catch {
      // silent
    }
  }

  async function handleSaveBusinessName() {
    try {
      await updateBusinessName.mutateAsync(businessNameValue);
      showToast('Display name updated');
    } catch {
      // silent
    }
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="h-8 w-48 animate-pulse rounded bg-bg-surface-2" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-2xl">
      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 rounded-lg bg-success px-4 py-3 text-sm font-medium text-bg-base shadow-lg">
          {toast}
        </div>
      )}

      <div>
        <h1 className="text-xl font-semibold text-text-primary">Custom Branding</h1>
        <p className="mt-1 text-sm text-text-secondary">
          Customize how your organization appears across the platform, emails, and reports.
        </p>
      </div>

      {/* Logo Upload Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Organization Logo</CardTitle>
          <CardDescription>
            Your logo appears in the platform sidebar, outbound emails, and generated reports.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current logo preview */}
          {branding?.hasLogo && branding.logoUrl && !selectedFile && (
            <div className="space-y-3">
              <p className="text-xs font-medium text-text-secondary uppercase tracking-wider">Current Logo</p>
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center rounded-lg border border-border-subtle bg-bg-base p-3">
                  <img
                    src={branding.logoUrl}
                    alt="Organization logo"
                    style={{ maxWidth: 200, maxHeight: 80, objectFit: 'contain' }}
                  />
                </div>
                <div className="text-xs text-text-secondary space-y-1">
                  {branding.filename && <p>{branding.filename}</p>}
                  {branding.size && <p>{formatBytes(branding.size)}</p>}
                </div>
              </div>
              {showDeleteConfirm ? (
                <div className="rounded-lg border border-danger/30 bg-danger/5 p-4 space-y-3">
                  <p className="text-sm text-text-primary">
                    Remove your logo? All emails and reports will revert to showing your organization name as text.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={handleDeleteLogo}
                      disabled={deleteLogo.isPending}
                    >
                      {deleteLogo.isPending ? 'Removing…' : 'Remove Logo'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-danger border-danger/40 hover:bg-danger/5"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <Trash2 className="mr-2 h-3.5 w-3.5" />
                  Remove Logo
                </Button>
              )}
            </div>
          )}

          {/* Upload zone */}
          <div>
            <p className="mb-2 text-xs font-medium text-text-secondary uppercase tracking-wider">
              {branding?.hasLogo ? 'Replace Logo' : 'Upload Logo'}
            </p>

            {/* Preview of selected file */}
            {previewUrl && (
              <div className="mb-3 flex items-center gap-3">
                <div className="flex items-center justify-center rounded-lg border border-accent-cyan/30 bg-bg-base p-3">
                  <img src={previewUrl} alt="Preview" style={{ maxWidth: 200, maxHeight: 80, objectFit: 'contain' }} />
                </div>
                <span className="text-xs text-text-secondary">{selectedFile?.name}</span>
              </div>
            )}

            <div
              className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-border-subtle bg-bg-base px-6 py-8 text-center transition-colors hover:border-accent-cyan/40 hover:bg-bg-surface"
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="mb-2 h-8 w-8 text-text-secondary" />
              <p className="text-sm font-medium text-text-primary">Drop your logo here, or click to browse</p>
              <p className="mt-1 text-xs text-text-secondary">PNG, JPG, or SVG · Max 500 KB</p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.svg"
              className="hidden"
              onChange={handleInputChange}
            />

            {uploadError && (
              <p className="mt-2 text-xs text-danger">{uploadError}</p>
            )}

            {selectedFile && (
              <div className="mt-3">
                <Button
                  size="sm"
                  onClick={handleUpload}
                  disabled={uploadLogo.isPending}
                >
                  {uploadLogo.isPending ? 'Uploading…' : 'Upload Logo'}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Business Name Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Display Name</CardTitle>
          <CardDescription>
            This name appears in email subject lines and as the sender name on all emails sent to vendors and contacts.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="business-name">Organization Display Name</Label>
            <Input
              id="business-name"
              value={businessNameValue}
              onChange={(e) => setBusinessNameValue(e.target.value)}
              placeholder="e.g. Crestview Health Systems"
              className="max-w-sm"
            />
          </div>
          <Button
            size="sm"
            onClick={handleSaveBusinessName}
            disabled={updateBusinessName.isPending || !businessNameValue.trim()}
          >
            {updateBusinessName.isPending ? 'Saving…' : 'Save'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
